// 확장자를 적는 이유: 이 파일과 아래 import 는 단위 테스트가 앱 번들 밖(node)에서 그대로 읽는다 —
// node ESM 은 상대 경로의 확장자를 추론하지 않는다.
import { MENU_STATE, menuUrlsOf } from './routeAccess.js'

/**
 * **세션 메뉴(`GET /mainMenu`)를 언제 읽고, 읽은 결과를 라우터 가드에 무엇으로 넘기나 — 판정 한 곳.** 의존성 없는 순수
 * 모듈이다(Vue·스토어·`@/` 별칭 금지). 스토어(`stores/menu`)가 이 함수들로 상태를 읽고 쓰며, 요청은 스토어 액션(경계) 하나가 한다.
 *
 * 메뉴 상태는 셋이다(스토어 `menu` 모듈의 state 모양):
 *
 * - `menuList`·`menuOwner` — 마지막으로 **읽은** 목록과 그 주인(`{ accountId, locale }`).
 * - `menuRead` — 아직 목록이 되지 않은 **최근 시도**(`{ accountId, locale, status }`, [MENU_READ]). 성공하면 지운다.
 *
 * 리뷰 M-1·L-2(R-L)가 이 모양을 정했다:
 *
 * - **실패를 기억한다** — 종전에는 실패가 아무것도 남기지 않아 다음 이동이 또 「모른다」가 되고, 이동할 때마다 다시 부르고
 *   기다리고 토스트를 띄웠다. 같은 계정·언어의 실패는 이 세션 동안 [MENU_STATE.UNAVAILABLE] 이다(증거 없음 — 막지 않는다,
 *   종전 동작). 다시 읽는 것은 언어를 바꿀 때(새 주인)뿐이다.
 * - **읽는 중이면 또 부르지 않는다** — 셸(SideNav)과 보류된 화면(App)이 같은 순간 읽기를 청해도 요청은 하나다. 진행 중 표지는
 *   Promise 가 아니라 이 값이다(스토어에 Promise 를 두지 않는다).
 */

/** 최근 시도의 상태 — 성공은 표지가 아니라 목록(`menuList`)이 말한다 */
export const MENU_READ = Object.freeze({
  LOADING: 'loading',
  FAILED: 'failed',
})

/** 두 주인(`{ accountId, locale }`)이 같은가 */
function sameOwner(a, b) {
  return !!a && !!b && a.accountId === b.accountId && a.locale === b.locale
}

/**
 * 시도 기록 — 주인에 상태를 붙인 새 객체. 순수.
 *
 * @param {{accountId: string, locale: ?string}} owner
 * @param {string} status [MENU_READ] 의 하나
 */
export function menuReadAttempt(owner, status) {
  return { accountId: owner.accountId, locale: owner.locale, status }
}

/**
 * **이 주인으로 메뉴를 부를 것인가** — 같은 주인으로 이미 읽었거나, 읽는 중이거나, 실패했으면 부르지 않는다. 순수.
 *
 * @param {{menuList: ?Array, menuOwner: ?object, menuRead: ?object}} state
 * @param {{accountId: string, locale: ?string}} owner
 */
export function needsMenuRead(state, owner) {
  if (state.menuList && sameOwner(state.menuOwner, owner)) return false
  if (state.menuRead && sameOwner(state.menuRead, owner)) return false

  return true
}

/**
 * **라우터 가드가 받는 이 세션의 메뉴**(`utils/routeAccess` 의 `session.menu`) — 화면 경로 목록이거나 [MENU_STATE] 의 하나. 순수.
 *
 * - 지금 계정으로 읽은 목록이 있으면 그 경로들. 언어를 바꿔 다시 읽는 중이거나 그 재읽기가 실패해도 앞 목록이 증거다 —
 *   화면 경로는 언어와 무관하다.
 * - 목록이 없고 지금 계정의 최근 시도가 실패면 [MENU_STATE.UNAVAILABLE](막지 않는다).
 * - 그 밖(읽는 중·읽은 적 없음·남의 계정)은 [MENU_STATE.UNKNOWN].
 *
 * @param {{menuList: ?Array, menuOwner: ?object, menuRead: ?object}} state
 * @param {string} accountId 지금 세션의 계정
 * @returns {Array<string>|string}
 */
export function sessionMenuOf(state, accountId) {
  if (state.menuList && state.menuOwner && state.menuOwner.accountId === accountId) return menuUrlsOf(state.menuList)

  const read = state.menuRead
  if (read && read.accountId === accountId && read.status === MENU_READ.FAILED) return MENU_STATE.UNAVAILABLE

  return MENU_STATE.UNKNOWN
}

/**
 * **메뉴를 기다리던 화면이 거절로 풀렸는가** — 앞 답이 「메뉴를 읽어야 답할 수 있다」(`needsMenu`)였고 새 답이 거절이면 참. 순수.
 *
 * 가드는 메뉴를 기다리지 않는다 — 셸을 먼저 그리고 화면 본문만 보류한다(`App.vue`). 메뉴가 오면 보류된 화면에 대한 답이
 * 정해지고, 거절이면 경계(`router`)가 안내로 바꿔 끼운다. 기다리지 않던 화면의 답이 바뀌는 일(로그아웃 등)은 이 함수가 답하지
 * 않는다 — 다음 이동의 가드와 로그인 감시자(`App.vue`)가 답한다.
 *
 * @param {?{allowed: boolean, needsMenu?: boolean}} previous 앞 답
 * @param {{allowed: boolean, needsMenu?: boolean}} next 새 답
 */
export function resolvesAsRefusal(previous, next) {
  return !!previous && previous.needsMenu === true && !next.allowed && !next.needsMenu
}
